import { Brain, Code, Github, Globe, Linkedin, User, X } from 'lucide-react';

// Developer Modal Component - About Rishi Nalem
export const DeveloperModal = ({ showDeveloperModal, setShowDeveloperModal, darkMode }) => {
  if (!showDeveloperModal) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={() => setShowDeveloperModal(false)}
    >
      <div
        className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-8">
          <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>About the Founder</h3>
          <button
            onClick={() => setShowDeveloperModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Developer Profile */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-300'}`}>
            <div className="flex items-center space-x-4 mb-4">
              <img
                src="/images/Rishi_Image.jpeg"
                alt="Pranay Rishi Nalem"
                className="w-16 h-16 rounded-full object-cover border-2 border-purple-500"
                onError={(e) => {
                  console.error('Failed to load profile image');
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h4 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Pranay Rishi Nalem
                </h4>
                <p className={`${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                  Full-Stack Developer & AI Enthusiast
                </p>
              </div>
            </div>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Passionate about creating innovative educational technology solutions that make learning accessible and engaging for everyone.
            </p>
          </div>

          {/* About Section */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              🚀 About This Project
            </h5>
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Nalem Study Circle is an advanced AI-powered learning platform designed to revolutionize personalized education. 
              Built with cutting-edge technologies and powered by OpenAI's GPT models, this platform creates custom learning 
              paths tailored to individual needs and learning styles.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Brain className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  AI-Generated Personalized Learning Paths
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Code className={`w-5 h-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Gamified Learning with Points & Achievements
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <User className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Integrated Pomodoro Timer & Study Tracking
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Globe className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Multi-Language Support & Accessibility
                </span>
              </div>
            </div>
          </div>

          {/* Technical Skills */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h5 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              <Code className="w-5 h-5 mr-2" />
              Technical Expertise
            </h5>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h6 className={`font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Frontend Development</h6>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• React.js & Modern JavaScript</li>
                  <li>• Responsive UI/UX Design</li>
                  <li>• Tailwind CSS & Animation</li>
                  <li>• Progressive Web Apps</li>
                </ul>
              </div>
              <div>
                <h6 className={`font-medium mb-2 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>AI & Backend</h6>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• OpenAI API Integration</li>
                  <li>• Node.js & Python</li>
                  <li>• Database Design</li>
                  <li>• RESTful API Development</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Philosophy */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-green-900/30 to-teal-900/30 border border-green-500/30' : 'bg-gradient-to-r from-green-100 to-teal-100 border border-green-300'}`}>
            <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
              💡 Development Philosophy
            </h5>
            <blockquote className={`italic ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
              "Education should be personalized, engaging, and accessible to everyone. By combining artificial intelligence 
              with thoughtful user experience design, we can create learning platforms that adapt to individual needs and 
              inspire lifelong learning."
            </blockquote>
            <p className={`mt-3 text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              - Rishi Nalem, Creator of Nalem Study Circle
            </p>
          </div>

          {/* Connect Section */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              🤝 Connect & Collaborate
            </h5>
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Interested in educational technology, AI applications, or collaborative projects? Let's connect!
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.linkedin.com/in/rishi-nalem/"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer pointer-events-auto ${darkMode ? 'bg-blue-800 text-blue-300 hover:bg-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                style={{ pointerEvents: 'auto' }}
              >
                <Linkedin className="w-4 h-4" />
                <span>LinkedIn</span>
              </a>
              <a
                href="https://github.com/pranayrishi"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer pointer-events-auto ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                style={{ pointerEvents: 'auto' }}
              >
                <Github className="w-4 h-4" />
                <span>GitHub</span>
              </a>
              <a
                href="https://www.rishinalem.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 cursor-pointer pointer-events-auto ${darkMode ? 'bg-purple-800 text-purple-300 hover:bg-purple-700' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                style={{ pointerEvents: 'auto' }}
              >
                <Globe className="w-4 h-4" />
                <span>Portfolio</span>
              </a>
            </div>
          </div>

          {/* Thank You */}
          <div className={`p-6 rounded-2xl text-center ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300'}`}>
            <h5 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
              🙏 Thank You for Using Nalem Study Circle
            </h5>
            <p className={`${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
              Your learning journey matters, and I'm honored to be part of it. Keep exploring, keep growing!
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowDeveloperModal(false)}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Developer Button Component for easy integration
export const DeveloperButton = ({ onClick, darkMode, className = "" }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'} shadow-lg hover:scale-105 flex items-center space-x-2 ${className}`}
  >
    <User className="w-4 h-4" />
    <span className="text-sm">About Founder</span>
  </button>
);

// Footer Developer Link Component
export const DeveloperFooterLink = ({ onClick, darkMode }) => (
  <button
    onClick={onClick}
    className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-300 flex items-center space-x-1`}
  >
    <User className="w-4 h-4" />
    <span>About Founder</span>
  </button>
);

export default DeveloperModal;